import { translateText } from '@/i18n'
import { pubInfo } from '@/server/home'
import { getUserInfo, internalWithdraw, userWithdrawNew } from '@/server/rebate'
import {
  getRobotList,
  getMyProfitLeaderboardRank,
  getSymbolCount,
  getUserFuturesException,
  getUserProfitLeaderboard,
  getUserProfitByWeekTop10,
  getStrategyHistoryList,
  getUserStrategyListAll,
  getFuturesProfitList
} from '@/server/robot'
import {
  getAllProfitEchart,
  getEverydayProfit,
  getMonthProfitEchart,
  getMyRank,
  getProfitInfo,
  getProfitRecordListByPage,
  getUserProfitDistribut,
  getUSDTRateByCode,
  getUserProfitLine
} from '@/server/user'
import {
  getDealRecord,
  getPaymentMethodsList,
  getPointCardLog,
  getAssetChangeRecordListByPage,
  getPropertyRecord,
  getRechargeQrCode,
  getTopUpRecord,
  getWithDrawRecord,
  applyRecharge,
  transferMoney
} from '@/server/property'
import {
  buyPointCardNew,
  getAuthCode,
  selectPointCart
} from '@/server/auth'

function responseError(result, label) {
  if (!result) return new Error(translateText(`${label}接口未返回数据`))
  if (result.success === false) return new Error(result.message || translateText(`${label}接口请求失败`))
  return null
}

async function settle(label, promise) {
  try {
    const result = await promise
    const error = responseError(result, label)
    if (error) throw error
    return {
      data: result && Object.prototype.hasOwnProperty.call(result, 'data') ? result.data : null,
      error: null
    }
  } catch (error) {
    return {
      data: null,
      error: error && error.message ? error.message : translateText(`${label}接口请求失败`)
    }
  }
}

function asArray(value) {
  if (Array.isArray(value)) return value
  if (value && Array.isArray(value.list)) return value.list
  if (value && Array.isArray(value.records)) return value.records
  return []
}

function walletOrderPage(value) {
  const page = value && typeof value === 'object' ? value : {}
  const total = Number(page.totalRow)
  return {
    rows: Array.isArray(page.list) ? page.list : [],
    total: Number.isFinite(total) && total >= 0 ? total : 0,
    allDeposit: page.allDeposit === undefined ? null : page.allDeposit
  }
}

function depositPeriodFilters(period) {
  const year = Number(period && period.year)
  const month = Number(period && period.month)
  if (!Number.isInteger(year) || !Number.isInteger(month) || month < 1 || month > 12) {
    return null
  }

  const pad = value => String(value).padStart(2, '0')
  const today = new Date()
  const monthEnd = new Date(year, month, 0)
  const isCurrentMonth = year === today.getFullYear() && month === today.getMonth() + 1
  const endDate = isCurrentMonth ? today : monthEnd

  return {
    dateType: '',
    startDate: `${year}-${pad(month)}-01`,
    endDate: `${endDate.getFullYear()}-${pad(endDate.getMonth() + 1)}-${pad(endDate.getDate())}`
  }
}

async function requestWalletOrderPage(category, pageNum, pageSize) {
  const isWithdrawal = category === 'withdraw'
  const result = await settle(
    isWithdrawal ? '提现记录' : '充值记录',
    isWithdrawal
      ? getWithDrawRecord(pageNum, pageSize)
      : getTopUpRecord(pageNum, pageSize)
  )
  const orderPage = walletOrderPage(result.data)
  return {
    ...orderPage,
    error: result.error,
    pageNum
  }
}

function collectErrors(results) {
  return Object.values(results)
    .map(result => result && result.error)
    .filter(Boolean)
}

export async function loadPublicSnapshot() {
  const [platform, leaders, trades] = await Promise.all([
    settle('平台公开数据', pubInfo(10)),
    settle('公开收益榜', getUserProfitByWeekTop10(0, 2, 1, 10)),
    settle('实时成交', getFuturesProfitList())
  ])

  return {
    platform: platform.data,
    leaders: asArray(leaders.data),
    trades: asArray(trades.data),
    errors: collectErrors({ platform, leaders, trades })
  }
}

export async function loadPortalSnapshot(selectedRobotType = null) {
  const now = new Date()
  const monthParams = {
    year: now.getFullYear(),
    month: now.getMonth() + 1
  }

  const [
    user,
    profit,
    today,
    monthTrend,
    allTrend,
    profitLine,
    rank,
    leaders,
    rate,
    robotList,
    strategyCapacity
  ] = await Promise.all([
    settle('用户信息', getUserInfo()),
    settle('累计收益', getProfitInfo()),
    settle('今日收益', getEverydayProfit(1)),
    settle('月度收益曲线', getMonthProfitEchart(monthParams)),
    settle('累计收益曲线', getAllProfitEchart()),
    settle('收益走势图', getUserProfitLine()),
    settle('我的收益排名', getMyRank()),
    settle('本周收益榜', getUserProfitByWeekTop10(0, 1, 0)),
    settle('USDT汇率', getUSDTRateByCode()),
    settle('机器人列表', getRobotList()),
    // App 机器人页固定请求 version=3，VIP/Pro 校验字段只在该版本返回。
    settle('策略可用名额', getSymbolCount(3))
  ])

  const exchangeRobots = asArray(robotList.data)
  const availableRobotTypes = [...new Set(
    exchangeRobots
      .map(item => Number(item && item.robot_type))
      .filter(Number.isFinite)
  )]
  const normalizedRobotType = Number(selectedRobotType)
  const activeRobotType = (
    selectedRobotType !== null &&
    selectedRobotType !== undefined &&
    availableRobotTypes.includes(normalizedRobotType)
  )
    ? normalizedRobotType
    : (availableRobotTypes[0] ?? null)
  const robotTypes = activeRobotType === null ? [] : [activeRobotType]

  const strategyResults = await Promise.all(
    robotTypes.map(async robotType => {
      const result = await settle(
        `机器人${robotType}策略`,
        getUserStrategyListAll(robotType, true, 10)
      )
      return {
        ...result,
        robotType
      }
    })
  )

  const strategies = strategyResults.flatMap(result =>
    asArray(result.data).map(item => ({
      ...item,
      __robotType: result.robotType
    }))
  )
  const strategyErrors = strategyResults
    .map(result => result.error)
    .filter(Boolean)

  return {
    user: user.data,
    profit: profit.data,
    today: asArray(today.data),
    monthTrend: asArray(monthTrend.data),
    allTrend: asArray(allTrend.data),
    profitLine: asArray(profitLine.data),
    rank: rank.data,
    leaders: asArray(leaders.data),
    rate: rate.data,
    exchangeRobots,
    exchangeError: robotList.error,
    strategyCapacity: strategyCapacity.data,
    activeRobotType,
    strategies,
    strategyErrors,
    period: monthParams,
    errors: collectErrors({
      user,
      profit,
      today,
      monthTrend,
      allTrend,
      profitLine,
      rank,
      leaders,
      rate,
      robotList,
      strategyCapacity,
      ...strategyResults.reduce((result, item, index) => {
        result[`strategy${index}`] = item
        return result
      }, {})
    })
  }
}

export async function loadExchangeStrategies(robotType) {
  const normalizedRobotType = Number(robotType)
  if (!Number.isFinite(normalizedRobotType)) {
    return {
      robotType: null,
      strategies: [],
      strategyErrors: ['交易所类型无效']
    }
  }

  const result = await settle(
    `机器人${normalizedRobotType}策略`,
    getUserStrategyListAll(normalizedRobotType, true, 10)
  )

  return {
    robotType: normalizedRobotType,
    strategies: asArray(result.data).map(item => ({
      ...item,
      __robotType: normalizedRobotType
    })),
    strategyErrors: result.error ? [result.error] : []
  }
}

export async function loadExchangeHistoryStrategies(robotType, pageNum = 1, pageSize = 10) {
  const normalizedRobotType = Number(robotType)
  const normalizedPage = Math.max(1, Number(pageNum) || 1)
  const normalizedPageSize = Math.max(1, Number(pageSize) || 10)
  if (!Number.isFinite(normalizedRobotType)) {
    return {
      robotType: null,
      strategies: [],
      total: 0,
      page: normalizedPage,
      pageSize: normalizedPageSize,
      strategyErrors: ['交易所类型无效']
    }
  }

  const result = await settle(
    `机器人${normalizedRobotType}历史策略`,
    getStrategyHistoryList(normalizedRobotType, normalizedPage, normalizedPageSize)
  )
  const payload = result.data && typeof result.data === 'object' ? result.data : {}
  const rows = Array.isArray(payload)
    ? payload
    : (Array.isArray(payload.list) ? payload.list : [])
  const totalValue = payload.length !== undefined
    ? payload.length
    : (payload.totalRow !== undefined ? payload.totalRow : payload.total)
  const total = Number(totalValue)

  return {
    robotType: normalizedRobotType,
    strategies: rows.map(item => ({
      ...item,
      __robotType: normalizedRobotType,
      __historyStrategy: true
    })),
    total: Number.isFinite(total) ? total : rows.length,
    page: normalizedPage,
    pageSize: normalizedPageSize,
    strategyErrors: result.error ? [result.error] : []
  }
}
export async function loadStrategyCapacity() {
  const result = await settle('策略可用名额', getSymbolCount(3))
  return {
    capacity: result.data && typeof result.data === 'object' ? result.data : null,
    error: result.error
  }
}
export async function loadEarningsMonth(year, month) {
  const result = await settle(
    '月度收益曲线',
    getMonthProfitEchart({ year, month })
  )

  return {
    rows: asArray(result.data),
    error: result.error
  }
}

export async function loadEarningsDetails(robotType = null) {
  const normalizedRobotType = Number(robotType)
  const queryRobotType = Number.isFinite(normalizedRobotType) ? normalizedRobotType : ''
  const [distribution, records] = await Promise.all([
    settle('收益构成', getUserProfitDistribut(3)),
    settle('最近结算', getProfitRecordListByPage(10, 6, 1, 0, 0, queryRobotType))
  ])
  const recordPage = records.data && typeof records.data === 'object'
    ? records.data
    : {}

  return {
    distribution: distribution.data && typeof distribution.data === 'object'
      ? distribution.data
      : {},
    records: asArray(recordPage),
    errors: collectErrors({ distribution, records })
  }
}
export async function loadEarningsLeaderboard(dateType = 1) {
  const normalizedDateType = [0, 1, 2].includes(Number(dateType))
    ? Number(dateType)
    : 1
  const [leaders, rank] = await Promise.all([
    settle('收益榜', getUserProfitLeaderboard(normalizedDateType, 0, 0)),
    settle('我的收益排名', getMyProfitLeaderboardRank(normalizedDateType, 0, 0, 0))
  ])

  return {
    leaders: asArray(leaders.data),
    rank: rank.data,
    errors: collectErrors({ leaders, rank })
  }
}

export async function loadAssetSummary() {
  const [user, rate] = await Promise.all([
    settle('用户资产', getUserInfo()),
    settle('USDT汇率', getUSDTRateByCode())
  ])

  return {
    user: user.data,
    rate: rate.data,
    errors: collectErrors({ user, rate })
  }
}

export async function loadAccountBalances() {
  const user = await settle('用户资产', getUserInfo())
  return {
    user: user.data,
    errors: collectErrors({ user })
  }
}

export async function loadDepositRecords(page = 1, pageSize = 10, period = null) {
  const pageNum = Math.max(1, Number(page) || 1)
  const normalizedPageSize = Math.max(1, Number(pageSize) || 10)
  const filters = depositPeriodFilters(period)
  const result = await settle(
    '燃料点卡记录',
    filters
      ? getDealRecord(pageNum, normalizedPageSize, filters)
      : getDealRecord(pageNum, normalizedPageSize)
  )
  const recordPage = walletOrderPage(result.data)
  return {
    ...recordPage,
    error: result.error,
    pageNum,
    pageSize: normalizedPageSize
  }
}

export async function loadAssetRecords(page = 1, pageSize = 10) {
  const [recharges, withdrawals, changes, deposits, pointCards] = await Promise.all([
    loadAssetOrderRecords('deposit', page, pageSize),
    loadAssetOrderRecords('withdraw', page, pageSize),
    settle('资产变动记录', getPropertyRecord(page, pageSize)),
    loadDepositRecords(page, pageSize),
    settle('点卡记录', getPointCardLog())
  ])

  return {
    recharges: recharges.rows,
    withdrawals: withdrawals.rows,
    rechargeTotal: recharges.total,
    withdrawalTotal: withdrawals.total,
    changes: asArray(changes.data),
    deposits: deposits.rows,
    depositTotal: deposits.total,
    pointCards: asArray(pointCards.data),
    errors: collectErrors({ recharges, withdrawals, changes, deposits, pointCards })
  }
}

export async function loadAssetOrderRecords(category, page = 1, pageSize = 10) {
  const normalizedCategory = category === 'withdraw' ? 'withdraw' : 'deposit'
  const pageNum = Math.max(1, Number(page) || 1)
  const normalizedPageSize = Math.max(1, Number(pageSize) || 10)
  return requestWalletOrderPage(normalizedCategory, pageNum, normalizedPageSize)
}

export async function loadCardTransferRecords(category, page = 1, pageSize = 10) {
  const normalizedCategory = category === 'fuelTransfer' ? 'fuelTransfer' : 'pointCard'
  const pageNum = Math.max(1, Number(page) || 1)
  const normalizedPageSize = Math.max(1, Number(pageSize) || 10)
  const result = await settle(
    normalizedCategory === 'fuelTransfer' ? '燃料划转记录' : '购买点卡记录',
    normalizedCategory === 'fuelTransfer'
      ? getAssetChangeRecordListByPage(0, 3, pageNum, normalizedPageSize)
      : getPointCardLog()
  )
  const pointCardRows = normalizedCategory === 'pointCard' ? asArray(result.data) : []
  const recordPage = normalizedCategory === 'fuelTransfer'
    ? walletOrderPage(result.data)
    : { rows: pointCardRows, total: pointCardRows.length }
  return {
    ...recordPage,
    error: result.error,
    pageNum,
    pageSize: normalizedPageSize,
    category: normalizedCategory
  }
}

export async function loadRechargeMethods() {
  const result = await settle('充值方式', getPaymentMethodsList())
  return {
    data: asArray(result.data),
    error: result.error
  }
}

export async function loadPointCardOptions() {
  const result = await settle('点卡额度', selectPointCart())
  return {
    data: asArray(result.data),
    error: result.error
  }
}

export function createRechargeOrder(amount, payType) {
  return applyRecharge(amount, payType)
}

export function loadRechargeOrderQrCode(uid) {
  return getRechargeQrCode(uid, 10)
}

export async function loadRobotRisk(robotType) {
  return settle(
    '机器人风险',
    getUserFuturesException(robotType, 10)
  )
}

export function transferBalance(from, to, amount) {
  return transferMoney(from, to, amount)
}

export function buyPointCard(cardAmount, giveCard) {
  return buyPointCardNew(cardAmount, giveCard)
}

export function withdrawBalance(address, amount, verificationCode, sendType = 1) {
  return userWithdrawNew(address, amount, verificationCode, sendType)
}

export function internalWithdrawBalance(address, amount, verificationCode, sendType = 1) {
  return internalWithdraw(address, amount, verificationCode, sendType)
}

export function sendWithdrawCode(address, codeType = 3, sendType = 1) {
  return getAuthCode(codeType, sendType, address)
}
